package one.healthium.paywall.ui.components

import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.unit.TextUnit
import androidx.compose.ui.unit.sp
import androidx.compose.ui.util.fastForEach
import one.healthium.domain.paywall.models.AttributedLine
import one.healthium.domain.paywall.models.TextStrokeStyle
import one.healthium.domain.paywall.models.Word
import one.healthium.paywall.ui.models.ProductUiModel
import one.healthium.ui.common.utils.toColor
import one.healthium.ui.common.utils.toFontWeight
import one.healthium.ui.common.utils.toPaddingValues
import one.healthium.ui.compose.theme.AppTheme

@OptIn(ExperimentalLayoutApi::class)
@Composable
fun AttributedLine(
    modifier: Modifier = Modifier,
    lineModel: AttributedLine,
    product: ProductUiModel?,
) {
    FlowRow(
        modifier = modifier.padding(lineModel.insets.toPaddingValues()),
    ) {
        lineModel.words.fastForEach { wordModel ->
            Word(
                wordModel = wordModel,
                product = product,
                defaultColor = lineModel.color?.toColor(),
            )
        }
    }
}

@Composable
fun Word(wordModel: Word, product: ProductUiModel?, defaultColor: Color?) {
    if (wordModel.template == null) return

    Text(
        text = wordModel.template.parse(product, stroke = wordModel.stroke == TextStrokeStyle.Through),
        color = wordModel.color?.toColor() ?: defaultColor ?: AppTheme.colors.text100,
        fontSize = wordModel.fontSize?.sp ?: 14.sp,
        fontWeight = wordModel.fontWeight.toFontWeight(),
        fontFamily = AppTheme.textStyles.body3.fontFamily, // TODO ?
        letterSpacing = wordModel.letterSpacing?.sp ?: TextUnit.Unspecified,
        lineHeight = wordModel.lineHeight?.sp ?: TextUnit.Unspecified,
        textDecoration = when (wordModel.stroke) {
            TextStrokeStyle.Through -> TextDecoration.LineThrough
            TextStrokeStyle.None -> TextDecoration.None
            null -> null
        },
    )
}

// possible values in template string:
// period
// period_num
// price
// OFFER[annually-90usd-base].period
// OFFER[annually-90usd-base].price
// OFFER[annually-90usd-base].price_monthly
// OFFER[annually-90usd-base].introductory_price
// OFFER[annually-90usd-base].introductory_price_monthly

private val offerTemplate = Regex("\\{(OFFER\\[(.*)]\\.)?([^}]*)\\}")

@Composable
internal fun String?.parse(product: ProductUiModel?, lowercase: Boolean = false, stroke: Boolean = false): String {
    return remember(this, product) {
        if (this.isNullOrEmpty()) return@remember ""
        if (product == null) return@remember this

        this.replace(offerTemplate) { matchResult ->
            val groups = matchResult.groupValues

            val offerId = groups[2].ifEmpty { null }
            val path = groups[3]
            val notEligibleForIntroOffer =  if (offerId != null) product.introductoryPrice(offerId).orEmpty() == "notEligibleForIntroOffer" else false
            val introductoryPrice = if (offerId != null) product.introductoryPrice(offerId).orEmpty() else ""
            val introductoryPriceMonthly = if (offerId != null) product.introductoryPriceMonthly(offerId).orEmpty() else ""
            val price = product.price(offerId).orEmpty()
            val priceMonthly = product.priceMonthly(offerId).orEmpty()

            when (path) {
                "period" -> product.period(offerId).lowercase(lowercase).orEmpty()
                "period_num" -> product.periodNum(offerId).lowercase(lowercase).orEmpty()
                "price" -> if (notEligibleForIntroOffer && stroke) "" else  price
                "price_monthly" -> priceMonthly
                "introductory_price" ->  if (notEligibleForIntroOffer) price else introductoryPrice
                "introductory_price_monthly" -> if (notEligibleForIntroOffer) priceMonthly else introductoryPriceMonthly
                else -> ""
            }
        }
    }
}

private fun String?.lowercase(lowercase: Boolean) = if (lowercase) this?.lowercase() else this